/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `Attendance` for the Caintra Connect frontend.
 * @module pages/Attendance
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/Attendance.module.css";

import { ReactComponent as QRCodeScanIcon } from "../assets/icons/AttendancePage/QRCodeScanIcon.svg";
import { ReactComponent as TitleIcon } from "../assets/icons/AttendancePage/TitleIcon.svg";
import { ReactComponent as MoreIcon } from "../assets/icons/AttendancePage/MoreIcon.svg";
import { AppContext } from "../context/AppContext.js";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import AttendancesTable from "../components/AttendancesTable/index.jsx";
import BusinessEventAttendancesTable from "../components/BusinessEventAttendancesTable/index.jsx";
import B2BEventAttendancesTable from "../components/B2BEventAttendancesTable/index.jsx";
import BreakfastEventAttendancesTable from "../components/BreakfastEventAttendancesTable/index.jsx";
import AttendancesCards from "../components/AttendancesCards/index.jsx";
import BusinessEventAttendancesCards from "../components/BusinessEventAttendancesCards/index.jsx";
import B2BEventAttendancesCards from "../components/B2BEventAttendancesCards/index.jsx";
import BreakfastEventAttendancesCards from "../components/BreakfastEventAttendancesCards/index.jsx";
import QRCodeScanModal from "../modals/QRCodeScanModal/index.jsx";
import AttendanceConfirmationModal from "../modals/AttendanceConfirmationModal/index.jsx";
import SearchBar from "../components/SearchBar/index.jsx";
import ResultsDropdown from "../components/ResultsDropdown";
import PageSelector from "../components/PageSelector";
import {
  getUserRoleData,
  getActiveData,
  getEventAttendees,
  getBusinessEventAttendees,
  getB2BEventAttendees,
  updateAttendance,
  handleDownloadCsv,
  handleToggleActive,
  updateAttendaceStatus,
  handleQRCodeAttendanceUpdate,
  handleQRCodeScanned,
  handleResultsLimitChange,
  handlePageChange,
  handleSort,
  handleSearch,
  filterAttendances,
  paginateAttendances,
} from "../logic/attendance/index.js";
import { getBreakfastEventAttendees } from "../logic/breakfastEvents/index.js";

import { ArrowDownward, KeyboardArrowDown } from "@mui/icons-material";
import { Backdrop, CircularProgress } from "@mui/material";

import { use, useState, useEffect } from "react";
import useDeviceDetection from "../hooks/useDeviceDetection";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { canAccessAttendance } from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";
import Select, { components } from "react-select";
import { DEFAULT_RESULTS_LIMIT } from "../utils/resultsLimitOptions.js";

const getSearchBarWidth = (deviceType) => {
  switch (deviceType) {
    case "laptop":
      return "91.1%";
    case "tablet":
      return "100%";
    case "mediumMobile":
      return "90%";
    case "smallMobile":
      return "100%";
    default:
      return "97.2%";
  }
};

const getSelectWidth = (deviceType) => {
  switch (deviceType) {
    case "tablet":
      return "50%";
    case "smallMobile":
      return "100%";
    case "mediumMobile":
      return "100%";
    default:
      return "100%";
  }
};

const DropdownIndicator = (props) => {
  const { selectProps } = props;

  return (
    <components.DropdownIndicator {...props}>
      <KeyboardArrowDown
        style={{
          transform: selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.3s ease-in-out",
        }}
      />
    </components.DropdownIndicator>
  );
};

const Attendances = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { changePermissions } = use(AppContext);
  const { isTablet, isSmallMobile, isMediumMobile, deviceType } =
    useDeviceDetection();
  const { allowed } = useRequireAccess(canAccessAttendance);

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventsList, setEventsList] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "nombre",
    direction: "ascending",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isBusinessEvent, setIsBusinessEvent] = useState(false);
  const [isB2BEvent, setIsB2BEvent] = useState(false);
  const [isBreakfastEvent, setIsBreakfastEvent] = useState(false);
  const [resultsLimit, setResultsLimit] = useState(DEFAULT_RESULTS_LIMIT);
  const [currentPage, setCurrentPage] = useState(1);
  const [isScanModalOpen, setIsScanModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isConfirmationSubmitting, setIsConfirmationSubmitting] =
    useState(false);
  const [currentAttendeeIndex, setCurrentAttendeeIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!!token && token.trim() !== "undefined") {
      getData();
    }
  }, []);

  const getData = async () => {
    setIsLoading(true);
    try {
      const permissions = await getUserRoleData(navigate, changePermissions);
      await getActiveData(navigate, setEventsList, permissions);
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    } finally {
      setIsLoading(false);
    }
  };

  const getEventAttendeesWrapper = async (event) => {
    setIsLoading(true);
    try {
      if (event.tipoEvento === "BUSINESS_EVENT") {
        setIsBusinessEvent(true);
        setIsB2BEvent(false);
        setIsBreakfastEvent(false);
        await getBusinessEventAttendees(event, navigate, setAttendanceData);
      } else if (event.tipoEvento === "B2B_EVENT") {
        setIsBusinessEvent(false);
        setIsB2BEvent(true);
        setIsBreakfastEvent(false);
        await getB2BEventAttendees(event, navigate, setAttendanceData);
      } else if (event.tipoEvento === "BREAKFAST_EVENT") {
        setIsBusinessEvent(false);
        setIsB2BEvent(false);
        setIsBreakfastEvent(true);
        await getBreakfastEventAttendees(event, navigate, setAttendanceData);
      } else {
        setIsBusinessEvent(false);
        setIsB2BEvent(false);
        setIsBreakfastEvent(false);
        await getEventAttendees(event, navigate, setAttendanceData);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadCsvWrapper = () => {
    handleDownloadCsv(attendanceData, selectedEvent);
  };

  const handleToggleActiveWrapper = async (attendeeIndex) => {
    await handleToggleActive(
      attendeeIndex,
      attendanceData,
      setCurrentAttendeeIndex,
      setIsConfirmationModalOpen,
      updateAttendaceStatusWrapper,
    );
  };

  const updateAttendaceStatusWrapper = async (attendeeIndex) => {
    const resolvedIndex =
      typeof attendeeIndex === "number" ? attendeeIndex : null;
    const submitFromModal = resolvedIndex === null && isConfirmationModalOpen;

    if (submitFromModal) {
      setIsConfirmationSubmitting(true);
    } else {
      setIsLoading(true);
    }
    try {
      await updateAttendaceStatus({
        attendeeIndex: resolvedIndex,
        currentAttendeeIndex,
        attendanceData,
        setAttendanceData,
        setCurrentAttendeeIndex,
        setIsConfirmationModalOpen,
        updateRegularAttendance: updateAttendanceWrapper,
        navigate,
      });
    } finally {
      if (submitFromModal) {
        setIsConfirmationSubmitting(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const updateAttendanceWrapper = async (userId, eventId, status) => {
    return await updateAttendance(userId, eventId, status, navigate);
  };

  const handleQRCodeScannedWrapper = async (status, userId, eventId) => {
    await handleQRCodeScanned(
      status,
      userId,
      eventId,
      selectedEvent,
      getEventAttendeesWrapper,
    );
  };

  const handleResultsLimitChangeWrapper = (selectedOption) => {
    handleResultsLimitChange(selectedOption, setResultsLimit, setCurrentPage);
  };

  const handlePageChangeWrapper = (direction) => {
    handlePageChange(
      direction,
      currentPage,
      attendanceData,
      resultsLimit,
      setCurrentPage,
    );
  };

  const handleSortWrapper = () => {
    handleSort(sortConfig, setSortConfig);
  };

  const renderArrow = () => {
    return (
      <ArrowDownward
        className={`${styles.arrowIcon} ${
          sortConfig.direction === "ascending" ? styles.rotated : ""
        }`}
      />
    );
  };

  const handleSearchWrapper = (e) => {
    handleSearch(e, setSearchQuery, setCurrentPage);
  };

  const handleQRCodeAttendanceUpdateWrapper = (userId, eventId) => {
    handleQRCodeAttendanceUpdate(
      userId,
      eventId,
      attendanceData,
      setAttendanceData,
    );
  };

  const toggleDropdown = (event) => {
    event.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const handleDownloadFromDropdown = () => {
    handleDownloadCsvWrapper();
    setShowDropdown(false);
  };

  const handleViewAttendanceFromDropdown = () => {
    setIsScanModalOpen(true);
    setShowDropdown(false);
  };

  const filteredAttendances = filterAttendances(
    attendanceData,
    searchQuery,
    isBusinessEvent,
    isB2BEvent,
    isBreakfastEvent,
  );

  const paginatedAttendancesList = paginateAttendances(
    filteredAttendances,
    currentPage,
    resultsLimit,
  );

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        {/* Page title and search bar */}
        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <TitleIcon className={styles.titleIcon} />
            <h1 className={styles.title}>{t("pages.attendance.header")}</h1>
          </div>
        </div>
      </div>

      <div className={styles.actionsContainer}>
        <span>{t("pages.attendance.selectEvent")}</span>
        <div className={styles.actions}>
          <Select
            options={eventsList}
            // className="select__control"
            components={{ DropdownIndicator }}
            placeholder={t("pages.attendance.eventPlaceholder")}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                // width: "100%",
                height: "40px",
                fontSize: "16px",
                cursor: "pointer",
                textAlign: "left",
                borderRadius: "8px",
                border: "1px solid #909092",
                // opacity: isDisabled ? 0.6 : 1,
              }),
              container: (baseStyles) => ({
                ...baseStyles,
                width: getSelectWidth(deviceType),
                // width: "120px",
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
              }),
              input: (baseStyles) => ({
                ...baseStyles,
              }),
              option: (baseStyles) => ({
                ...baseStyles,
                cursor: "pointer",
              }),
              indicatorSeparator: () => ({ display: "none" }),
              // menu: (baseStyles) => ({
              //   ...baseStyles,
              //   zIndex: 9999,
              //   position: "absolute",
              //   width: "110px",
              // }),
              menuPortal: (baseStyles) => ({
                ...baseStyles,
                zIndex: 9999,
              }),
            }}
            menuPortalTarget={document.body}
            isSearchable={false}
            // value={value}
            onChange={(selectedOption) => {
              logger.info("selectedOption: ", { data: selectedOption });
              setSelectedEvent(selectedOption);
              getEventAttendeesWrapper(selectedOption);
              setSearchQuery("");
            }}
            getOptionValue={(option) => option._id}
            formatOptionLabel={(option) => <span>{option.nombre}</span>}
          />
          {!isSmallMobile && (
            <>
              <button
                className={styles.downloadButton}
                onClick={handleDownloadCsvWrapper}
              >
                {t("pages.attendance.printList")}
              </button>
              <button
                className={`${styles.viewAttendanceButton} ${
                  !selectedEvent ? styles.disabled : ""
                }`}
                onClick={() => setIsScanModalOpen(true)}
                disabled={!selectedEvent}
              >
                <QRCodeScanIcon />
                {t("pages.attendance.attendance")}
              </button>
            </>
          )}
          {isSmallMobile && (
            <div className={styles.dropdownContainer}>
              <button
                className={styles.dropdownButton}
                onClick={toggleDropdown}
              >
                <MoreIcon />
              </button>
              {showDropdown && (
                <div className={styles.dropdownMenu}>
                  <button
                    className={styles.dropdownItem}
                    onClick={handleDownloadFromDropdown}
                  >
                    {t("pages.attendance.printList")}
                  </button>
                  <button
                    className={`${styles.dropdownItem} ${
                      !selectedEvent ? styles.dropdownItemDisabled : ""
                    }`}
                    onClick={handleViewAttendanceFromDropdown}
                    disabled={!selectedEvent}
                  >
                    {t("pages.attendance.attendance")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {selectedEvent && (
        <div className={styles.searchSection}>
          <SearchBar
            value={searchQuery}
            onChange={handleSearchWrapper}
            placeholder={t("pages.attendance.searchAttendees")}
            width={getSearchBarWidth(deviceType)}
          />
        </div>
      )}

      <div className={styles.attendanceList}>
        {paginatedAttendancesList.length === 0 && (
          <div className={styles.noResults}>
            <p>{t("pages.attendance.noResults")}</p>
          </div>
        )}

        {paginatedAttendancesList.length > 0 &&
          !isBusinessEvent &&
          !isB2BEvent &&
          !isBreakfastEvent && (
            <>
              {isTablet || isSmallMobile || isMediumMobile ? (
                <AttendancesCards
                  attendances={paginatedAttendancesList}
                  handleToggleActive={handleToggleActiveWrapper}
                />
              ) : (
                <AttendancesTable
                  attendances={paginatedAttendancesList}
                  onSort={handleSortWrapper}
                  handleToggleActive={handleToggleActiveWrapper}
                  renderArrow={renderArrow}
                />
              )}
            </>
          )}

        {paginatedAttendancesList.length > 0 && isBusinessEvent && (
          <>
            {isTablet || isSmallMobile || isMediumMobile ? (
              <BusinessEventAttendancesCards
                attendances={paginatedAttendancesList}
                handleToggleActive={handleToggleActiveWrapper}
              />
            ) : (
              <BusinessEventAttendancesTable
                attendances={paginatedAttendancesList}
                onSort={handleSortWrapper}
                handleToggleActive={handleToggleActiveWrapper}
                renderArrow={renderArrow}
              />
            )}
          </>
        )}

        {paginatedAttendancesList.length > 0 && isB2BEvent && (
          <>
            {isTablet || isSmallMobile || isMediumMobile ? (
              <B2BEventAttendancesCards
                attendances={paginatedAttendancesList}
                handleToggleActive={handleToggleActiveWrapper}
              />
            ) : (
              <B2BEventAttendancesTable
                attendances={paginatedAttendancesList}
                onSort={handleSortWrapper}
                handleToggleActive={handleToggleActiveWrapper}
                renderArrow={renderArrow}
              />
            )}
          </>
        )}

        {paginatedAttendancesList.length > 0 && isBreakfastEvent && (
          <>
            {isTablet || isSmallMobile || isMediumMobile ? (
              <BreakfastEventAttendancesCards
                attendances={paginatedAttendancesList}
                handleToggleActive={handleToggleActiveWrapper}
              />
            ) : (
              <BreakfastEventAttendancesTable
                attendances={paginatedAttendancesList}
                onSort={handleSortWrapper}
                handleToggleActive={handleToggleActiveWrapper}
                renderArrow={renderArrow}
              />
            )}
          </>
        )}
      </div>

      <div className={styles.footer}>
        {/* Number of results dropdown */}
        <ResultsDropdown
          onResultsLimitChange={handleResultsLimitChangeWrapper}
        />

        {/* Page selector */}
        <PageSelector
          currentPage={currentPage}
          totalPages={Math.ceil(filteredAttendances.length / resultsLimit)}
          onPageChange={handlePageChangeWrapper}
        />
      </div>
      <QRCodeScanModal
        open={isScanModalOpen}
        onClose={() => setIsScanModalOpen(false)}
        selectedEvent={selectedEvent}
        onQRCodeScanned={handleQRCodeScannedWrapper}
      />
      <AttendanceConfirmationModal
        open={isConfirmationModalOpen}
        onCancel={() => setIsConfirmationModalOpen(false)}
        onSuccess={updateAttendaceStatusWrapper}
        message={t("pages.attendance.confirmationMessage")}
        isSubmitting={isConfirmationSubmitting}
      />
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 2,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoading}
      >
        <CircularProgress size={60} thickness={4} sx={{ color: "#4eabe7" }} />
      </Backdrop>
    </div>
  );
};

export default Attendances;
